/**
 * OrganizationEdit Component
 * Edit form for an existing organization's profile. Only accessible to users
 * who are an owner or admin of the organization, or an ecosystem admin of
 * the active tenant. Located at /organizations/:id/edit.
 *
 * Key behaviors:
 * - Loads the organization's current data and populates the form on mount
 * - Loads enum values (sectors, regions, programming types) in parallel
 * - Performs role-based access control: redirects unauthorized users to the view page
 * - Validates all fields before saving (same rules as onboarding)
 * - Handles logo upload to S3 as a separate step before the main update
 * - Converts newline-delimited offerings/needs text to arrays on save
 * - Shows ManageManagers and ManageRelationships sub-components for owners/admins
 * - On success, redirects to the org detail page after a brief delay
 */
import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Card,
    Spinner,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

import { useOrganizationApi } from '@/shared/services/organizationApi';
import { useMembership } from '@/shared/contexts/MembershipContext';
import useFullAuth from '@/features/auth/hooks/useFullAuth';
import {
    normalizeUrl,
    hasUnsupportedScheme,
} from '@/shared/utils/normalizeUrl';
import { mergeTimezoneOption } from '@/shared/constants/timezones';

import ManageManagers from './components/ManageManagers';
import ManageRelationships from './components/ManageRelationships';
import './Organizations.css';

export default function OrganizationEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    // useFullAuth provides both the Firebase user and the MongoDB profile
    // (including role), unlike useAuth which only provides Firebase user.
    const { user: currentUser, loading: authLoading } = useFullAuth();
    // Ecosystem-admin override. The legacy `User.role === 'admin'` no longer
    // exists post-P4a (User.role is 'user' | 'platformAdmin'); the
    // ecosystem-admin signal moved to the per-tenant EcosystemMembership
    // row. The server's org PUT/DELETE already authorize ecosystem admins;
    // this client check mirrors that boundary (review finding 9).
    const { role: membershipRole, isMember } = useMembership();
    const isEcosystemAdmin = isMember && membershipRole === 'admin';

    // ---- Organization Data ----
    const [organization, setOrganization] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        primarySector: '',
        secondarySectors: [],
        description: '',
        email: '',
        website: '',
        imageUrl: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        timezone: '',
        regionsServed: [],
        programmingTypes: [],
        offerings: '', // Stored as newline-delimited text in the form
        needs: '', // Stored as newline-delimited text in the form
    });

    // ---- Enum Options (fetched from API) ----
    const [sectors, setSectors] = useState([]);
    const [regions, setRegions] = useState([]);
    const [taxonomyProgrammingTypes, setTaxonomyProgrammingTypes] = useState(
        []
    );

    // ---- UI State ----
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    // The user's role within this specific organization (owner/admin/member).
    // Used to conditionally render ManageManagers and ManageRelationships.
    const [currentUserOrgRole, setCurrentUserOrgRole] = useState(null);
    const fileInputRef = useRef(null);

    const orgApi = useOrganizationApi();

    // If the org's stored timezone falls outside the curated list (e.g. a
    // non-US tenant), pin it to the top so the select always has a matching
    // option instead of silently showing no selection. Memoized on the
    // loaded value, not `formData.timezone`, so it stays stable while the
    // user is actively choosing a different one.
    const timezoneOptions = useMemo(
        () => mergeTimezoneOption(organization?.location?.timezone),
        [organization]
    );

    // Load the organization's current data and enum options in parallel on mount.
    // The organization data is used to pre-populate the form fields so the user
    // sees the current values and can edit them in place.
    // Note: offerings and needs are stored as arrays in MongoDB but are joined
    // into newline-delimited strings for the textarea inputs.
    useEffect(() => {
        const loadData = async () => {
            try {
                const [orgData, s, r, p] = await Promise.all([
                    orgApi.fetchOrganization(id),
                    orgApi.getSectors(),
                    orgApi.getRegions(),
                    orgApi.getProgrammingTypes(),
                ]);

                setOrganization(orgData);
                setSectors(s);
                setRegions(r);
                setTaxonomyProgrammingTypes(p);

                // Populate form with existing data.
                // Arrays (offerings, needs) are joined into newline-separated strings
                // for textarea display; they'll be split back on save.
                setFormData({
                    name: orgData.name || '',
                    primarySector: orgData.primarySector || '',
                    secondarySectors: orgData.secondarySectors || [],
                    description: orgData.description || '',
                    email: orgData.email || '',
                    website: orgData.website || '',
                    imageUrl: orgData.imageUrl || '',
                    address: orgData.location?.address || '',
                    city: orgData.location?.city || '',
                    state: orgData.location?.state || '',
                    zipCode: orgData.location?.zipCode || '',
                    timezone: orgData.location?.timezone || '',
                    regionsServed: orgData.regionsServed || [],
                    programmingTypes: orgData.programmingTypes || [],
                    offerings: (orgData.offerings || []).join('\n'),
                    needs: (orgData.needs || []).join('\n'),
                });

                // If the org already has a logo, show it as the image preview
                if (orgData.imageUrl) {
                    setImagePreview(orgData.imageUrl);
                }
            } catch (err) {
                setError('Failed to load organization');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id, orgApi]);

    // Authorization check: once both auth and org data are loaded, verify
    // the current user has edit permissions. A user can edit if:
    // 1. They are an owner or admin member of this organization, OR
    // 2. They are an ecosystem admin of the active tenant.
    // If neither condition is met, redirect to the read-only org detail page.
    // Also stores the user's org-level permission role for conditional
    // rendering of ManageManagers and ManageRelationships sections.
    useEffect(() => {
        if (!authLoading && !loading && organization && currentUser) {
            const managerInfo = organization.managers?.find(
                (m) => m.user === currentUser._id
            );
            const canEdit =
                managerInfo &&
                ['owner', 'admin'].includes(managerInfo.permissionRole);

            if (!canEdit && !isEcosystemAdmin) {
                navigate(`/organizations/${id}`, { replace: true });
            } else {
                setCurrentUserOrgRole(managerInfo?.permissionRole || null);
            }
        }
    }, [
        authLoading,
        loading,
        organization,
        currentUser,
        id,
        navigate,
        isEcosystemAdmin,
    ]);

    // ---- Form Input Handlers ----
    // These are identical to OrganizationOnboarding's handlers.
    // Both clear validation errors on edit so error messages disappear
    // as the user corrects the field.

    // Generic text/select input handler
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (validationErrors[name]) {
            setValidationErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    // Checkbox group toggle (secondarySectors, regionsServed, programmingTypes)
    const handleMultiSelect = (name, value) => {
        setFormData((prev) => {
            const current = prev[name] || [];
            if (current.includes(value)) {
                return { ...prev, [name]: current.filter((v) => v !== value) };
            }
            return { ...prev, [name]: [...current, value] };
        });
        if (validationErrors[name]) {
            setValidationErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    // Logo file selection: validates type/size, creates a data URL preview,
    // and stores the File object for S3 upload during save.
    const handleImageChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('Image must be less than 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);

        setFormData((prev) => ({ ...prev, imageFile: file }));
    }, []);

    // Full-form validation (runs on save, unlike onboarding which validates per-step).
    // Required fields: name, primarySector, description, regionsServed.
    // Optional fields with format validation: email (regex), website (a bare
    // domain like "historycolorado.org" is auto-corrected to https:// on
    // submit rather than rejected here -- only an explicit non-http(s)
    // scheme, e.g. "ftp://", is flagged).
    const validate = () => {
        const errors = {};

        if (!formData.name?.trim()) {
            errors.name = 'Organization name is required';
        }
        if (!formData.primarySector) {
            errors.primarySector = 'Primary sector is required';
        }
        if (!formData.description?.trim()) {
            errors.description = 'Description is required';
        } else if (formData.description.length > 2000) {
            errors.description =
                'Description must be less than 2000 characters';
        }
        if (formData.regionsServed.length === 0) {
            errors.regionsServed = 'Please select at least one region';
        }
        if (
            formData.email &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        ) {
            errors.email = 'Please enter a valid email address';
        }
        if (formData.website && hasUnsupportedScheme(formData.website)) {
            errors.website = 'Website must start with http:// or https://';
        }
        if (!formData.timezone) {
            errors.timezone = 'Timezone is required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Save handler: validates, optionally uploads new logo to S3, then
    // PUTs the updated org data to the API. Same two-phase pattern as
    // OrganizationOnboarding: upload image first, then submit org data.
    // On success, shows a message and redirects to the org detail page.
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        setSaving(true);
        setError('');

        try {
            let imageUrl = formData.imageUrl;

            // Phase 1: Upload new logo to S3 if the user selected a new file
            if (formData.imageFile) {
                setUploadingImage(true);
                try {
                    const uploadResult = await orgApi.uploadLogo(
                        formData.imageFile
                    );
                    imageUrl = uploadResult.imageUrl;
                } catch (uploadErr) {
                    setError(
                        uploadErr.response?.data?.message ||
                            'Failed to upload logo. Please try again.'
                    );
                    setSaving(false);
                    setUploadingImage(false);
                    return;
                }
                setUploadingImage(false);
            }

            // Phase 2: Build the update payload and send to the API.
            // 'type' is a legacy field required by the server schema.
            const submitData = {
                name: formData.name.trim(),
                type: formData.primarySector,
                primarySector: formData.primarySector,
                secondarySectors: formData.secondarySectors,
                description: formData.description.trim(),
                email: formData.email?.trim() || undefined,
                website: normalizeUrl(formData.website?.trim()) || undefined,
                imageUrl: imageUrl || undefined,
                location: {
                    address: formData.address?.trim() || undefined,
                    city: formData.city?.trim() || undefined,
                    state: formData.state?.trim() || undefined,
                    zipCode: formData.zipCode?.trim() || undefined,
                    timezone: formData.timezone,
                    // No form field edits this; round-tripped from the
                    // loaded org so the server's Object.assign(organization,
                    // value) doesn't silently wipe it on every save. Guarded
                    // against a malformed/empty point (defense in depth --
                    // Joi's .length(2) would reject anything else).
                    coordinates:
                        organization?.location?.coordinates?.coordinates
                            ?.length === 2
                            ? organization.location.coordinates
                            : undefined,
                },
                regionsServed: formData.regionsServed,
                programmingTypes: formData.programmingTypes,
                offerings: formData.offerings
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
                needs: formData.needs
                    .split('\n')
                    .map((s) => s.trim())
                    .filter(Boolean),
            };

            await orgApi.updateOrganization(id, submitData);
            setSuccess('Organization updated successfully!');
            setTimeout(() => {
                navigate(`/organizations/${id}`);
            }, 1500);
        } catch (err) {
            setError(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    'Failed to update organization. Please try again.'
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading || authLoading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (!organization) {
        return (
            <Container className="py-5 text-center">
                <p className="text-danger">Organization not found</p>
                <Button as={Link} to="/directory" variant="primary">
                    Back to Ecosystem
                </Button>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Edit {organization.name} | RILE Connect</title>
            </Helmet>
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col lg={8}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h1 className="text-primary mb-0">
                                Edit Organization
                            </h1>
                            <Link
                                to={`/organizations/${id}`}
                                className="btn btn-outline-secondary"
                            >
                                Cancel
                            </Link>
                        </div>

                        {error && <Alert variant="danger">{error}</Alert>}
                        {success && <Alert variant="success">{success}</Alert>}

                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <Form noValidate onSubmit={handleSubmit}>
                                    {/* Basic Info */}
                                    <h5 className="text-primary mb-3">
                                        Basic Information
                                    </h5>

                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Organization Name *
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            isInvalid={!!validationErrors.name}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors.name}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Primary Sector *
                                        </Form.Label>
                                        <Form.Select
                                            name="primarySector"
                                            value={formData.primarySector}
                                            onChange={handleChange}
                                            isInvalid={
                                                !!validationErrors.primarySector
                                            }
                                        >
                                            <option value="">
                                                Select a sector
                                            </option>
                                            {sectors.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors.primarySector}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Secondary Sectors
                                        </Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {sectors.map((s) => (
                                                <Form.Check
                                                    key={s}
                                                    type="checkbox"
                                                    id={`sector-${s}`}
                                                    label={s}
                                                    checked={formData.secondarySectors.includes(
                                                        s
                                                    )}
                                                    onChange={() =>
                                                        handleMultiSelect(
                                                            'secondarySectors',
                                                            s
                                                        )
                                                    }
                                                    disabled={
                                                        s ===
                                                        formData.primarySector
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Description *</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            isInvalid={
                                                !!validationErrors.description
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors.description}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            {formData.description.length}/2000
                                            characters
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Organization Logo
                                        </Form.Label>
                                        <div className="d-flex align-items-center gap-3">
                                            {imagePreview && (
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="logo-preview-md"
                                                />
                                            )}
                                            <div>
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    onChange={handleImageChange}
                                                    accept="image/*"
                                                    className="d-none"
                                                />
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() =>
                                                        fileInputRef.current?.click()
                                                    }
                                                    disabled={uploadingImage}
                                                >
                                                    {imagePreview
                                                        ? 'Change Image'
                                                        : 'Choose Image'}
                                                </Button>
                                                {imagePreview && (
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="text-danger"
                                                        onClick={() => {
                                                            setImagePreview(
                                                                null
                                                            );
                                                            setFormData(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    imageFile:
                                                                        null,
                                                                    imageUrl:
                                                                        '',
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </Form.Group>

                                    <hr className="my-4" />

                                    {/* Contact & Location */}
                                    <h5 className="text-primary mb-3">
                                        Contact & Location
                                    </h5>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Contact Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            isInvalid={!!validationErrors.email}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors.email}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Website</Form.Label>
                                        <Form.Control
                                            type="url"
                                            name="website"
                                            value={formData.website}
                                            onChange={handleChange}
                                            isInvalid={
                                                !!validationErrors.website
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors.website}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Street Address</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>City</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>State</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="state"
                                                    value={formData.state}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>
                                                    ZIP Code
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="zipCode"
                                                    value={formData.zipCode}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-4">
                                        <Form.Label>Timezone *</Form.Label>
                                        <Form.Select
                                            name="timezone"
                                            value={formData.timezone}
                                            onChange={handleChange}
                                            isInvalid={
                                                !!validationErrors.timezone
                                            }
                                        >
                                            <option value="">
                                                Select a timezone
                                            </option>
                                            {timezoneOptions.map((tz) => (
                                                <option
                                                    key={tz.value}
                                                    value={tz.value}
                                                >
                                                    {tz.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {validationErrors.timezone}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label>
                                            Regions Served *
                                        </Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {regions.map((r) => (
                                                <Form.Check
                                                    key={r}
                                                    type="checkbox"
                                                    id={`region-${r}`}
                                                    label={r}
                                                    checked={formData.regionsServed.includes(
                                                        r
                                                    )}
                                                    onChange={() =>
                                                        handleMultiSelect(
                                                            'regionsServed',
                                                            r
                                                        )
                                                    }
                                                />
                                            ))}
                                        </div>
                                        {validationErrors.regionsServed && (
                                            <div className="text-danger small mt-1">
                                                {validationErrors.regionsServed}
                                            </div>
                                        )}
                                    </Form.Group>

                                    <hr className="my-4" />

                                    {/* Programs */}
                                    <h5 className="text-primary mb-3">
                                        Programs & Partnerships
                                    </h5>

                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Programming Types
                                        </Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {taxonomyProgrammingTypes.map(
                                                (p) => (
                                                    <Form.Check
                                                        key={p}
                                                        type="checkbox"
                                                        id={`program-${p}`}
                                                        label={p}
                                                        checked={formData.programmingTypes.includes(
                                                            p
                                                        )}
                                                        onChange={() =>
                                                            handleMultiSelect(
                                                                'programmingTypes',
                                                                p
                                                            )
                                                        }
                                                    />
                                                )
                                            )}
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>What We Offer</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="offerings"
                                            value={formData.offerings}
                                            onChange={handleChange}
                                            placeholder="One item per line"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label>What We Need</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="needs"
                                            value={formData.needs}
                                            onChange={handleChange}
                                            placeholder="One item per line"
                                        />
                                    </Form.Group>

                                    {/* Submit */}
                                    <div className="d-flex justify-content-end gap-2">
                                        <Button
                                            as={Link}
                                            to={`/organizations/${id}`}
                                            variant="outline-secondary"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={saving || uploadingImage}
                                        >
                                            {uploadingImage ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        className="me-2"
                                                    />
                                                    Uploading Logo...
                                                </>
                                            ) : saving ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        className="me-2"
                                                    />
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save Changes'
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>

                        {/* Manage Managers Section: only visible to org owners,
                            org admins, and ecosystem admins of the active
                            tenant. Allows adding and removing the people
                            permitted to manage the organization. Permission
                            roles are not edited here: ownership moves through
                            transfer, and admin through add/remove. */}
                        {(currentUserOrgRole === 'owner' ||
                            currentUserOrgRole === 'admin' ||
                            isEcosystemAdmin) && (
                            <div className="mt-4">
                                <ManageManagers
                                    organizationId={id}
                                    currentUserId={currentUser._id}
                                    currentUserPermissionRole={
                                        currentUserOrgRole || 'admin'
                                    }
                                    isSystemAdmin={isEcosystemAdmin}
                                    onSelfRemoved={() =>
                                        navigate(`/organizations/${id}`, {
                                            replace: true,
                                        })
                                    }
                                />
                            </div>
                        )}

                        {/* Manage Relationships Section: allows org owners/admins to
                            create, view, and remove org-to-org relationships in Neo4j.
                            These relationships power the network graph visualization. */}
                        {(currentUserOrgRole === 'owner' ||
                            currentUserOrgRole === 'admin' ||
                            isEcosystemAdmin) && (
                            <div className="mt-4">
                                <ManageRelationships
                                    organizationId={id}
                                    organizationName={organization?.name || ''}
                                    canEdit={
                                        currentUserOrgRole === 'owner' ||
                                        currentUserOrgRole === 'admin' ||
                                        isEcosystemAdmin
                                    }
                                />
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </>
    );
}
